/**
 * RoutingActionCard - Card de preview para cada ação da sequência
 * 
 * Exibe informações visuais da ação (texto, mídia, delay, dados)
 * com botões de edição e remoção. Suporta drag & drop.
 */

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  GripVertical,
  Pencil,
  X,
  MessageSquare,
  Image,
  Video,
  Music,
  FileText,
  Timer,
  Link,
  Building2,
  User,
  Tag,
  MinusCircle,  // v2.1: Ícone para remover tag (mesmo do Flow)
  Star,
  LayoutDashboard,
  Users,  // v2.2: Ícone para fila global
  MousePointerClick,  // v2.3: Ícone para botão com link
  FileSignature,  // v2.4: Ícone para template
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { RoutingActionItem, actionTypeConfig } from './types';
import { RoutingActionType } from '@/pages/tenant/RoutingRules';
import { useMediaGallery } from '@/hooks/useMediaGallery';

// =====================================================
// INTERFACES
// =====================================================

interface RoutingActionCardProps {
  item: RoutingActionItem;
  index: number;
  onEdit: () => void;
  onRemove: () => void;
  // Dados para exibição de nomes
  departmentName?: string;
  agentName?: string;
  tagName?: string;
  phaseName?: string;
  queueName?: string;  // v2.2: Nome da fila global
}

/**
 * Extrai apenas o nome do arquivo de um caminho completo do Storage
 * @param path - Caminho completo (ex: MediaGallery/.../1765411913878_imagem-01.png)
 * @returns Nome do arquivo (ex: imagem-01.png)
 */
function extractFileName(path: string | undefined): string {
  if (!path) return 'Arquivo não selecionado';
  
  // Se já for uma URL completa, extrai o último segmento
  const segments = path.split('/');
  const fullName = segments[segments.length - 1];
  
  // Remove prefixo de timestamp se existir (padrão: 1765411913878_)
  const timestampPattern = /^\d{13}_/;
  if (timestampPattern.test(fullName)) {
    return fullName.replace(timestampPattern, '');
  }
  
  return fullName;
}

// Mapeamento de ícones por tipo
const typeIcons: Record<RoutingActionType | 'delay', React.ElementType> = {
  send_text: MessageSquare,
  send_image: Image,
  send_video: Video,
  send_audio: Music,
  send_document: FileText,
  send_link: Link,
  assign_department: Building2,
  assign_agent: User,
  add_tag: Tag,
  remove_tag: MinusCircle,  // v2.1: Remover tag
  set_priority: Star,
  assign_phase: LayoutDashboard,
  assign_global_queue: Users,  // v2.2: Fila global
  send_button_link: MousePointerClick,  // v2.3: Botão com link
  send_template: FileSignature,  // v2.4: Template WhatsApp
  delay: Timer,
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function RoutingActionCard({
  item,
  index,
  onEdit,
  onRemove,
  departmentName,
  agentName,
  tagName,
  phaseName,
  queueName,  // v2.2: Nome da fila global
}: RoutingActionCardProps) {
  const { getMediaUrl } = useMediaGallery({});
  const actionKey = item.action_type as RoutingActionType | 'delay';
  const config = actionTypeConfig[actionKey];
  const Icon = typeIcons[actionKey];

  // =====================================================
  // RENDERIZAÇÃO DE CONTEÚDO
  // =====================================================

  /**
   * Renderiza preview baseado no tipo de ação
   */
  const renderContent = () => {
    const value = item.action_value;
    
    // Caso especial para delay (usa delay_seconds ao invés de action_type)
    if (item.delay_seconds && item.delay_seconds > 0 && item.action_type === ('delay' as any)) {
      return (
        <p className="text-sm">
          Aguardar <strong>{item.delay_seconds}</strong> segundos
        </p>
      );
    }

    switch (item.action_type) {
      // ========== AÇÕES DE ENVIO ==========
      case 'send_text': {
        const message = value.message as string;
        return (
          <p className="text-sm line-clamp-2">
            {message || 'Mensagem não definida'}
          </p>
        );
      }

      case 'send_image':
      case 'send_video':
      case 'send_audio':
      case 'send_document': {
        const mediaUrl = value.media_url as string;
        const caption = value.caption as string;
        const filename = value.filename as string;
        
        if (item.action_type === 'send_image' && mediaUrl) {
          const url = mediaUrl.startsWith('http') ? mediaUrl : getMediaUrl(mediaUrl);
          return (
            <div className="flex gap-3">
              <img
                src={url || mediaUrl}
                alt="Preview"
                className="w-14 h-14 object-cover rounded-md"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{extractFileName(mediaUrl)}</p>
                {caption && (
                  <p className="text-xs text-muted-foreground/70 mt-1 line-clamp-1">
                    Legenda: {caption}
                  </p>
                )}
              </div>
            </div>
          );
        }
        
        return (
          <div className="flex gap-2 items-center">
            <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground truncate">
                {filename || extractFileName(mediaUrl)}
              </p>
              {caption && (
                <p className="text-xs text-muted-foreground/70 line-clamp-1">
                  Legenda: {caption}
                </p>
              )}
            </div>
          </div>
        );
      }

      case 'send_link': {
        const url = (value.link_url || value.url) as string;
        const title = value.title as string;
        return (
          <div className="space-y-1">
            <p className="text-xs font-medium truncate">{title || 'Link'}</p>
            <p className="text-xs text-muted-foreground truncate">{url}</p>
          </div>
        );
      }

      // ========== AÇÕES DE DADOS ==========
      case 'assign_department':
        return (
          <p className="text-sm">
            Atribuir: <strong>{departmentName || 'Departamento'}</strong>
          </p>
        );

      case 'assign_agent':
        return (
          <p className="text-sm">
            Atribuir: <strong>{agentName || 'Agente'}</strong>
          </p>
        );

      case 'add_tag':
        return (
          <p className="text-sm">
            Adicionar: <strong>{tagName || 'Tag'}</strong>
          </p>
        );

      // v2.1: Remover tag de contato
      case 'remove_tag': {
        const mode = item.action_value.remove_tag_mode as string;
        if (mode === 'all') {
          return (
            <p className="text-sm text-red-600">
              Remover <strong>todas as tags</strong>
            </p>
          );
        }
        return (
          <p className="text-sm">
            Remover: <strong>{tagName || 'Tag'}</strong>
          </p>
        );
      }

      case 'set_priority':
        return (
          <p className="text-sm">
            Definir prioridade: <strong>{(value.priority as number) || 5}</strong>
          </p>
        );

      case 'assign_phase':
        return (
          <p className="text-sm">
            Mover para: <strong>{phaseName || 'Fase'}</strong>
          </p>
        );

      // v2.2: Distribuir via fila global
      case 'assign_global_queue':
        return (
          <p className="text-sm">
            Distribuir via: <strong>{queueName || 'Fila'}</strong>
          </p>
        );

      // v2.3: Botão com link clicável
      case 'send_button_link': {
        const btnLabel = item.action_value.button_label as string;
        const btnUrl = item.action_value.link_url as string;
        return (
          <div className="space-y-1">
            <p className="text-sm line-clamp-1">
              {(item.action_value.message as string) || 'Mensagem'}
            </p>
            <div className="flex items-center gap-1.5 text-xs text-violet-600">
              <MousePointerClick className="h-3 w-3" />
              <span className="font-medium">{btnLabel || 'Botão'}</span>
              <span className="text-muted-foreground truncate">→ {btnUrl}</span>
            </div>
          </div>
        );
      }

      // v2.4: Template WhatsApp
      case 'send_template': {
        const tplName = item.action_value.template_name as string;
        const tplLang = item.action_value.template_language as string;
        const tplParams = item.action_value.template_params as any;
        const paramsCount = (tplParams?.body_params?.filter(Boolean)?.length || 0) + 
                           (tplParams?.header_params?.filter(Boolean)?.length || 0);
        return (
          <div className="space-y-1">
            <p className="text-sm font-medium">{tplName || 'Template'}</p>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>{tplLang || 'pt_BR'}</span>
              {paramsCount > 0 && (
                <Badge variant="outline" className="text-xs h-4 px-1">
                  {paramsCount} param{paramsCount > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        );
      }

      default:
        return <p className="text-sm text-muted-foreground">Ação desconhecida</p>;
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className={cn(
      "flex items-start gap-3 p-3 border rounded-lg transition-all",
      "bg-card hover:bg-muted/30",
      "border-border/60"
    )}>
      {/* Handle de drag */}
      <div className="cursor-grab active:cursor-grabbing pt-0.5">
        <GripVertical className="h-5 w-5 text-muted-foreground/50" />
      </div>

      {/* Número do item */}
      <Badge variant="outline" className="shrink-0 font-medium">
        {index + 1}
      </Badge>

      {/* Ícone do tipo - Design profissional */}
      <div className={cn(
        "flex items-center justify-center w-8 h-8 rounded-md shrink-0 border",
        config.bgColor,
        config.borderColor
      )}>
        <Icon className={cn("h-4 w-4", config.color)} />
      </div>

      {/* Conteúdo */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={cn("text-xs font-medium uppercase tracking-wide", config.color)}>
            {config.label}
          </span>
        </div>
        {renderContent()}
      </div>

      {/* Ações */}
      <div className="flex gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onEdit}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={onRemove}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
